import { XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  OpsOrderCarrier,
  OpsOrderCarrierService,
} from '@/graphql/generatedTypes';
import i18n from '@/utils/i18n';

import { carrierLabel } from './carrierLabels';

export type CarrierEntry = {
  carrier_service: OpsOrderCarrierService;
  carrier: OpsOrderCarrier;
};

export type CarrierAvailability = {
  carrier: OpsOrderCarrier;
  services: OpsOrderCarrierService[];
};

type Props = {
  value: CarrierEntry[];
  onChange: (next: CarrierEntry[]) => void;
  /**
   * When provided, the per-row carrier dropdown is filtered to carriers that
   * support that row's service. When omitted, every carrier is offered and
   * validation is deferred to the backend.
   */
  availableCarrierServices?: CarrierAvailability[];
  /**
   * Optional cascade preview — for a row with no explicit value, the resolved
   * default (e.g. from country config) is shown as muted helper text.
   */
  effectiveDefaults?: CarrierEntry[];
  readOnly?: boolean;
};

const SERVICE_ORDER: OpsOrderCarrierService[] = [
  OpsOrderCarrierService.HomeDelivery,
  OpsOrderCarrierService.Dropoff,
];

const CARRIER_OPTIONS: OpsOrderCarrier[] = [
  OpsOrderCarrier.Dpd,
  OpsOrderCarrier.Dhl,
  OpsOrderCarrier.Ups,
  // In-house / manual carrier. In the per-store config this is intersected
  // with the store's available_carrier_services (so it only appears where the
  // backend allows it); in the by-country config no availability is passed, so
  // it is selectable for any country.
  OpsOrderCarrier.Manual,
  // Off-platform shipping. Available in every country, but never for DROPOFF —
  // see CARRIER_UNSUPPORTED_SERVICES.
  OpsOrderCarrier.Others,
];

// Services a carrier can never serve, enforced client-side too so the
// by-country config (which passes no availability) cannot offer a combination
// the backend rejects.
const CARRIER_UNSUPPORTED_SERVICES: Partial<
  Record<OpsOrderCarrier, OpsOrderCarrierService[]>
> = {
  [OpsOrderCarrier.Others]: [OpsOrderCarrierService.Dropoff],
};

function serviceLabel(service: OpsOrderCarrierService): string {
  switch (service) {
    case OpsOrderCarrierService.HomeDelivery:
      return i18n.t('Carrier:serviceHomeDelivery');
    case OpsOrderCarrierService.Dropoff:
      return i18n.t('Carrier:serviceDropoff');
    case OpsOrderCarrierService.Pickup:
      return i18n.t('Carrier:servicePickup');
  }
}

function carriersForService(
  service: OpsOrderCarrierService,
  availability: CarrierAvailability[] | undefined,
): OpsOrderCarrier[] {
  const supported = CARRIER_OPTIONS.filter(
    (c) => !CARRIER_UNSUPPORTED_SERVICES[c]?.includes(service),
  );
  if (!availability) return supported;
  return supported.filter((c) =>
    availability.some((a) => a.carrier === c && a.services.includes(service)),
  );
}

export function DefaultCarrierConfig({
  value,
  onChange,
  availableCarrierServices,
  effectiveDefaults,
  readOnly,
}: Props) {
  const valueByService = new Map<OpsOrderCarrierService, OpsOrderCarrier>(
    value.map((e) => [e.carrier_service, e.carrier]),
  );
  const effectiveByService = new Map<OpsOrderCarrierService, OpsOrderCarrier>(
    (effectiveDefaults ?? []).map((e) => [e.carrier_service, e.carrier]),
  );

  const setCarrierFor = (
    service: OpsOrderCarrierService,
    carrier: OpsOrderCarrier,
  ) => {
    const next = value.filter((e) => e.carrier_service !== service);
    next.push({ carrier_service: service, carrier });
    onChange(next);
  };

  const clearCarrierFor = (service: OpsOrderCarrierService) => {
    onChange(value.filter((e) => e.carrier_service !== service));
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="tw:w-1/3">
            {i18n.t('Carrier:service')}
          </TableHead>
          <TableHead>{i18n.t('Carrier:carrier')}</TableHead>
          <TableHead className="tw:w-16" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {SERVICE_ORDER.map((service) => {
          const explicit = valueByService.get(service) ?? null;
          const inherited = explicit
            ? null
            : (effectiveByService.get(service) ?? null);
          const carriers = carriersForService(
            service,
            availableCarrierServices,
          );
          const hasAnyCarrier = carriers.length > 0;
          return (
            <TableRow key={service}>
              <TableCell className="tw:font-medium">
                {serviceLabel(service)}
              </TableCell>
              <TableCell>
                <div className="tw:flex tw:flex-col tw:gap-1">
                  <Select
                    value={explicit ?? undefined}
                    onValueChange={(v) =>
                      setCarrierFor(service, v as OpsOrderCarrier)
                    }
                    disabled={readOnly || !hasAnyCarrier}
                  >
                    <SelectTrigger className="tw:w-48">
                      <SelectValue
                        placeholder={
                          inherited ?? i18n.t('Carrier:selectCarrier')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {carriers.map((c) => (
                        <SelectItem key={c} value={c}>
                          {carrierLabel(c)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!explicit && inherited && (
                    <span className="tw:text-muted-foreground tw:text-xs">
                      {i18n.t('Carrier:inheritedHint')}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {explicit && !readOnly && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => clearCarrierFor(service)}
                    aria-label={i18n.t('Carrier:clearRow')}
                  >
                    <XIcon />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
